<template>
  <div class="action-grid">
    <!-- Row 1 — Undo · Floor Override ‖ No Sale -->
    <div class="action-grid__pair">
      <OutcomeButton
        tone="info"
        icon="undo"
        label="Undo"
        hotkey="F9"
        density="compact"
        :disabled="!canUndo || busy"
        @click="emit('undo')"
      />
      <OutcomeButton
        tone="neutral"
        icon="flag"
        hotkey="F8"
        density="compact"
        :disabled="!canFloorOverride || busy || finalized"
        @click="emit('floor-override')"
      >
        Floor<br>Override
      </OutcomeButton>
    </div>

    <OutcomeButton
      class="action-grid__no-sale"
      tone="no-sale"
      icon="ban"
      label="No Sale"
      hotkey="F4"
      density="compact"
      :disabled="finalized || busy"
      @click="emit('finalize', 'NOT-SOLD')"
    />

    <!-- Rows 2–3 — Floor (spanning) ‖ Sell, Offer -->
    <OutcomeButton
      class="action-grid__floor"
      tone="floor"
      big
      icon="users"
      :label="floorLabel"
      :sub="floorSub"
      :disabled="finalized || busy"
      @click="emit('take-floor')"
    />
    <OutcomeButton
      class="action-grid__sell"
      tone="sell"
      big
      icon="check"
      label="Sell"
      hotkey="F7"
      :sub="sellSub"
      :disabled="finalized || !hasBids || busy"
      @click="emit('finalize', 'SOLD')"
    />
    <OutcomeButton
      class="action-grid__offer"
      tone="offer"
      icon="tag"
      label="Offer"
      hotkey="F6"
      :disabled="finalized || !hasBids || busy"
      @click="emit('finalize', 'OFFER')"
    />

    <!-- Row 4 — Current bid ‖ Rep offer -->
    <div class="action-grid__slot action-grid__slot--left">
      <CurrentBidCard :amount="currentBid" :leader-source="leaderSource" :leader-badge="leaderBadge" />
    </div>
    <div class="action-grid__slot action-grid__slot--right">
      <RepOfferCard :rep-name="repName" />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import OutcomeButton from '../OutcomeButton/OutcomeButton.vue';
import CurrentBidCard from '../CurrentBidCard/CurrentBidCard.vue';
import RepOfferCard from '../RepOfferCard/RepOfferCard.vue';

// The outcome cluster, laid out position-for-position with the incumbent
// console so a clerk's hand lands in the same places.
//
// It is ONE grid with four equal rows and the Floor button spanning two of
// them, rather than two columns side by side. Two columns drift: the rows stop
// lining up the moment a label wraps, and the clerk's eye has to re-find the
// row it was on.
//
// Every action here used to talk to the runtime itself — take the floor,
// override it, undo, and pause-then-finalize. All five are events now. The
// pause-before-finalize dance in particular was never this component's to know:
// it emits `finalize` with the outcome, and the application freezes bidding,
// captures what the clerk saw, and raises the confirm panel.
defineOptions({ name: 'ActionGrid' });

const props = defineProps({
  // The standing high bid.
  currentBid: { type: Number, default: 0 },
  // What taking the floor would bid.
  nextBid: { type: Number, default: 0 },
  // floor | online | prebid — where the standing bid came from.
  leaderSource: { type: String, default: 'floor' },
  // The high bidder's badge; null when nobody has bid.
  leaderBadge: { type: String, default: null },
  // SOLD | NOT-SOLD | OFFER once the lot is settled, else the live status.
  status: { type: String, default: null },
  // Whether an undo has anything to reverse. The engine is authoritative and
  // rejects true no-ops; this only has to stop the obvious ones.
  canUndo: { type: Boolean, default: false },
  // A real buyer holds the high bid, so it can be handed to the floor.
  canFloorOverride: { type: Boolean, default: false },
  // An action is in flight — every button locks rather than double-firing.
  busy: { type: Boolean, default: false },
  // The selling rep's name, for the offer tile. Null hides it.
  repName: { type: String, default: null },
});

const emit = defineEmits(['undo', 'floor-override', 'take-floor', 'finalize']);

const hasBids = computed(() => props.leaderBadge != null);

// Once the lot is settled there is no floor left to take, so the button stops
// offering the action and states the outcome instead — the clerk still needs to
// read what happened, and the biggest button on the screen is where they look.
const SETTLED_LABELS = { SOLD: 'Sold', 'NOT-SOLD': 'No Sale', OFFER: 'Offer' };
const finalized = computed(() => SETTLED_LABELS[props.status] != null);
const floorLabel = computed(() => SETTLED_LABELS[props.status] ?? 'Floor');
const floorSub = computed(() =>
  finalized.value
    ? `$${(props.currentBid || 0).toLocaleString()}`
    : `Take floor · $${(props.nextBid || 0).toLocaleString()}`
);
const sellSub = computed(() => (hasBids.value ? `$${(props.currentBid || 0).toLocaleString()}` : 'No bids'));
</script>

<style scoped>
.action-grid {
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: grid;
  /* minmax(0, 1fr) rather than the default minmax(auto, 1fr): auto holds the
     buttons' intrinsic width, and the grid then spills rightward under the
     increment rail instead of shrinking with its column. */
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  grid-template-rows: repeat(4, 1fr);
  gap: var(--p-action-grid-gap);
}

.action-grid__pair {
  grid-column: 1;
  grid-row: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  min-height: 0;
  min-width: 0;
  gap: var(--p-action-grid-pair-gap);
}

/* Every button gives up its own minimum height to the grid's row track, which
   is what keeps the two columns on the same horizontal lines. */
.action-grid :deep(.outcome-button) {
  min-height: 0;
}

.action-grid__no-sale {
  grid-column: 2;
  grid-row: 1;
}

.action-grid__floor {
  grid-column: 1;
  grid-row: 2 / 4;
}

.action-grid__sell {
  grid-column: 2;
  grid-row: 2;
}

.action-grid__offer {
  grid-column: 2;
  grid-row: 3;
}

.action-grid__slot {
  grid-row: 4;
  display: flex;
  min-height: 0;
}

.action-grid__slot--left {
  grid-column: 1;
}

.action-grid__slot--right {
  grid-column: 2;
}
</style>
